import { getKey } from './auth.jsx';

const BASE = '';

// MAPTRIUM Hub Control Plane (license + sync backend).
// This is intentionally a separate URL from BASE; the atlas-studio Azure
// Functions API doesn't proxy it. The browser calls it directly over HTTPS
// (CORS allowlist includes maptrium.com). Will become api.maptrium.com once
// DNS delegation lands.
const HUB_URL = import.meta.env?.VITE_HUB_URL || 'https://maptrium-hub.salmondesert-b463dd73.eastus2.azurecontainerapps.io';

async function hubReq(method, path, hubKey, body) {
  const headers = { 'Content-Type': 'application/json' };
  if (hubKey) headers['X-Admin-Key'] = hubKey;
  const opts = { method, headers };
  if (body !== undefined) opts.body = JSON.stringify(body);
  const r = await fetch(`${HUB_URL}${path}`, opts);
  const text = await r.text();
  let data; try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
  if (!r.ok) {
    const err = new Error((data && data.detail) || (data && data.error) || `HTTP ${r.status}`);
    err.status = r.status; err.data = data;
    throw err;
  }
  return data;
}

async function req(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  const k = getKey();
  if (k) headers['X-Admin-Key'] = k;
  try {
    const u = JSON.parse(sessionStorage.getItem('studio_user') || 'null');
    if (u && u.email) headers['X-Admin-Email'] = u.email;
  } catch {}
  const opts = { method, headers };
  if (body !== undefined) opts.body = JSON.stringify(body);
  const r = await fetch(`${BASE}/api${path}`, opts);
  const text = await r.text();
  let data;
  try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
  if (!r.ok) {
    const err = new Error((data && data.detail) || (data && data.error) || `HTTP ${r.status}`);
    err.status = r.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  // Auth
  authCheck: (body) => req('POST', '/auth/check', body || {}),
  login: (email, key) => req('POST', '/auth/check', { email: (email || '').trim(), key: (key || '').trim() }),
  emailSignup: (b) => req('POST', '/auth/signup', b),
  emailLogin:  (b) => req('POST', '/auth/login',  b),
  requestPasswordReset: (b) => req('POST', '/auth/reset-request', b),
  confirmPasswordReset: (b) => req('POST', '/auth/reset-confirm', b),
  acceptInvite: (b) => req('POST', '/auth/accept-invite', b),
  googleSignin: (b) => req('POST', '/auth/google', b),
  inviteUser: (b) => req('POST', '/users-invite', b),
  adminUsersSummary: () => req('GET', '/users-summary'),
  downloadFile: (jobId, filename) => req('POST', `/jobs/${jobId}/download/${encodeURIComponent(filename)}`),
  // Users (admin)
  listUsers: () => req('GET', '/users'),
  createUser: (u) => req('POST', '/users', u),
  updateUser: (id, u) => req('PUT', `/users/${id}`, u),
  deleteUser: (id) => req('DELETE', `/users/${id}`),
  // Self
  me: () => req('GET', '/me'),
  rotateMyKey: (custom) => req('POST', '/me/key', custom ? { access_key: custom } : {}),
  // Jobs
  listJobs: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return req('GET', `/jobs${qs ? '?' + qs : ''}`);
  },
  getJob: (id) => req('GET', `/jobs/${id}`),
  createJob: (j) => req('POST', '/jobs', j),
  // Upload raw bytes to Blob staging (returns { blob_path, size_bytes, filename })
  uploadFile: async (jobId, file) => {
    const headers = {};
    const k = getKey(); if (k) headers['X-Admin-Key'] = k;
    try {
      const u = JSON.parse(sessionStorage.getItem('studio_user') || 'null');
      if (u && u.email) headers['X-Admin-Email'] = u.email;
    } catch {}
    const qs = new URLSearchParams({ filename: file.name }).toString();
    const r = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/upload?${qs}`, { method: 'POST', headers, body: file });
    if (!r.ok) {
      const text = await r.text();
      let data; try { data = JSON.parse(text); } catch { data = { raw: text }; }
      throw new Error((data && (data.detail || data.error)) || `Upload failed: HTTP ${r.status}`);
    }
    return await r.json();
  },
  // Pipeline
  profileFile: (jobId) => req('POST', `/jobs/${jobId}/profile`, {}),
  setMapping: (jobId, mapping) => req('POST', `/jobs/${jobId}/mapping`, mapping),
  runJob: (jobId, options) => req('POST', `/jobs/${jobId}/run`, options),
  // Pipeline v2 (5-agent stack) — only called when flags.pipeline_v2 is true
  runJobV2: (jobId, options) => req('POST', `/jobs/${jobId}/run-v2`, options),
  // Maptrium Core pipeline — opt-in, feature-flagged via MAPTRIUM_CORE_URL env.
  //   project_type: 'drone' | 'lidar' | 'survey' | 'ortho'  (or raw worker: 'odm'|'pdal'|'ml'|'fused')
  //   quality: 'fast' | 'standard' | 'full'
  runMaptrium: (jobId, { maptrium_type, quality = 'standard', inputs, options } = {}) =>
    req('POST', `/jobs/${jobId}/run-maptrium`,
        { maptrium_type, quality, inputs, options }),
  checkMaptrium: (jobId, { type, options } = {}) =>
    req('POST', `/jobs/${jobId}/maptrium-check`, { type, options }),
  // Request a SAS URL for direct-to-blob upload (large files).
  uploadSas: (jobId, filename) =>
    req('POST', `/jobs/${jobId}/upload-sas`, { filename }),
  // Upload directly to Azure Blob via SAS URL — returns on success, throws on failure.
  uploadToSas: async (sasUrl, file) => {
    const r = await fetch(sasUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    });
    if (!r.ok) {
      const text = await r.text();
      throw new Error(`SAS upload failed: HTTP ${r.status} ${text.slice(0, 200)}`);
    }
    return true;
  },
  // Per-user feature flags (env-var allowlisted on backend)
  getFlags: () => req('GET', '/flags/me'),
  // Mr Pilot conversational layer over agent_lineage
  askPilot: (jobId, question) => req('POST', `/jobs/${jobId}/pilot`, { question }),
  // Tiers / usage
  getTiers: () => req('GET', '/tiers'),
  getUsage: () => req('GET', '/usage'),
  // Admin
  adminStats: () => req('GET', '/stats/admin'),
  // Audit (admin)
  adminAudit: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return req('GET', `/audit${qs ? '?' + qs : ''}`);
  },
  // Billing
  billingStatus: () => req('GET', '/billing/status'),
  billingCheckout: (tier) => req('POST', '/billing/checkout', { tier }),
  billingPortal: () => req('POST', '/billing/portal', {}),
  // AGOL
  getAgol: () => req('GET', '/me/agol'),
  saveAgol: (s) => req('PUT', '/me/agol', s),
  testAgol: (s) => req('POST', '/me/agol/test', s || {}),
  saveAgolForUser: (id, s) => req('PUT', `/users/${id}/agol`, s),
  publishAgol: (jobId) => req('POST', `/jobs/${jobId}/publish-agol`),
  // Per-job add-on (one-time charge unlocking AGOL/shapefile/FGDB/CSV for PAYG users)
  addonCheckout: (jobId, addon) => req('POST', '/billing/addon-checkout', { job_id: jobId, addon }),
  // Symbology
  symbologyCatalog: () => req('GET', '/symbology/catalog'),
  // Ali — natural-language → symbology JSON (Sonnet, custom_palette-capable).
  // Body: { prompt, columns, project_type?, client_brief?, job_id? }
  aliAssist: (body) => req('POST', '/ali-assist', body),
  // Preview map before running the full pipeline. First call geocodes +
  // caches; subsequent calls reuse the cache (no MAPTRIUM credits charged).
  previewMap: (jobId, body) => req('POST', `/jobs/${jobId}/preview-map`, body || {}),
  // Project types (industry-standard presets + glossary)
  projectTypes: () => req('GET', '/project-types'),
  dataRequirements: () => req('GET', '/data-requirements'),
  legal: () => req('GET', '/legal'),
  // MAPTRIUM Hub (license + sync Control Plane). Browser calls the Hub
  // Container App directly over CORS. Admin key stored in localStorage per
  // browser; see src/pages/AdminHub.jsx for the user-facing UI.
  //
  // The old desktopListTiers / desktopIssueLicense / desktopListSubscribers
  // routes pointed at a defunct Azure Functions scaffold that preceded the
  // PASETO v4 Hub. They were removed when AdminDesktop.jsx was replaced by
  // AdminHub.jsx. If anything in the repo still references them, swap to
  // the hub* helpers below.
  hubGet:  (path, hubKey)       => hubReq('GET',  path, hubKey),
  hubPost: (path, hubKey, body) => hubReq('POST', path, hubKey, body),
  hubDel:  (path, hubKey)       => hubReq('DELETE', path, hubKey),

  // Customer-facing desktop endpoints (used by MAPTRIUM Desktop clients).
  desktopMyUsage:    () => req('GET', '/desktop/my-usage'),
  desktopMyLicense:  () => req('POST', '/desktop/my-license', {}),
  desktopSyncSeats:  (seats) => req('POST', '/desktop/users/sync', { seats }),
  desktopBrandingLogo: () => req('GET', '/desktop/branding/logo'),
};

export default api;
